/**
 * 多状态展示组件
 *
 * @experimental 此组件尚未在消费项目中广泛使用，API 可能调整。
 *
 * 统一的页面状态展示：空状态、错误状态、加载状态、403/404/500、成功结果等。
 * 比 ErrorBoundary 更全面，适用于页面级别的状态展示。
 */

import { Result, Spin, theme } from 'antd';
import type React from 'react';

/**
 * 状态类型
 */
export type StatusType =
  | 'empty'
  | 'error'
  | 'loading'
  | 'success'
  | '403'
  | '404'
  | '500';

/**
 * StatusShow 组件属性
 */
export interface StatusShowProps {
  /** 状态类型 */
  status: StatusType;
  /** 标题 */
  title?: string;
  /** 描述文本 */
  description?: string;
  /** 自定义图标 */
  icon?: React.ReactNode;
  /** 操作按钮 */
  extra?: React.ReactNode;
  /** 加载中的提示文本 */
  loadingTip?: string;
  /** 自定义样式 */
  style?: React.CSSProperties;
  /** 自定义类名 */
  className?: string;
  /** 子内容（在 loading 状态下包裹显示） */
  children?: React.ReactNode;
}

/** 默认文案配置 */
const DEFAULT_TEXTS: Record<
  StatusType,
  { title: string; description: string }
> = {
  empty: {
    title: '暂无数据',
    description: '没有可展示的内容',
  },
  error: {
    title: '加载失败',
    description: '数据加载失败，请稍后重试',
  },
  loading: {
    title: '加载中',
    description: '正在加载数据...',
  },
  success: {
    title: '操作成功',
    description: '',
  },
  '403': {
    title: '无访问权限',
    description: '抱歉，您没有权限访问此页面',
  },
  '404': {
    title: '页面不存在',
    description: '抱歉，您访问的页面不存在',
  },
  '500': {
    title: '服务器错误',
    description: '抱歉，服务器发生错误，请稍后重试',
  },
};

/**
 * 映射到 antd Result 的 status
 */
function mapToResultStatus(
  status: StatusType,
): 'success' | 'error' | 'info' | 'warning' | 403 | 404 | 500 {
  switch (status) {
    case 'empty':
      return 'info';
    case 'error':
      return 'error';
    case 'success':
      return 'success';
    case '403':
      return 403;
    case '404':
      return 404;
    case '500':
      return 500;
    default:
      return 'info';
  }
}

/**
 * 多状态展示组件
 *
 * @example
 * ```tsx
 * // 空状态
 * <StatusShow status="empty" />
 *
 * // 错误状态 + 重试按钮
 * <StatusShow
 *   status="error"
 *   description="数据加载失败"
 *   extra={<Button onClick={reload}>重试</Button>}
 * />
 *
 * // 加载状态包裹内容
 * <StatusShow status="loading" loadingTip="加载中...">
 *   <div>实际内容</div>
 * </StatusShow>
 *
 * // 403 无权限
 * <StatusShow
 *   status="403"
 *   extra={<Button type="primary" onClick={() => history.push('/')}>返回首页</Button>}
 * />
 * ```
 */
export function StatusShow({
  status,
  title,
  description,
  icon,
  extra,
  loadingTip,
  style,
  className,
  children,
}: StatusShowProps): React.ReactElement {
  const { token } = theme.useToken();
  const defaults = DEFAULT_TEXTS[status];

  // Loading 状态特殊处理
  if (status === 'loading') {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
          padding: token.paddingLG,
          ...style,
        }}
        className={className}
      >
        <Spin tip={loadingTip ?? title ?? defaults.title} size="large">
          {children ?? <div style={{ padding: '50px 0' }} />}
        </Spin>
      </div>
    );
  }

  return (
    <div style={{ padding: token.paddingLG, ...style }} className={className}>
      <Result
        status={mapToResultStatus(status)}
        title={title ?? defaults.title}
        subTitle={description ?? defaults.description}
        icon={icon}
        extra={extra}
      />
    </div>
  );
}
